/**
 * Outfit shop catalog — mirrors backend routes/outfits.py (server is price authority).
 */

// Rarity tiers derived from price
export const rarityOf = (price) => {
  if (price >= 250) return 'legendary';
  if (price >= 100) return 'epic';
  if (price > 0) return 'rare';
  return 'common';
};

export const RARITY_META = {
  common: { label: 'Common', color: '#9ca3af', bg: 'rgba(156,163,175,0.15)' },
  rare: { label: 'Rare', color: '#38bdf8', bg: 'rgba(56,189,248,0.15)' },
  epic: { label: 'Epic', color: '#a78bfa', bg: 'rgba(167,139,250,0.18)' },
  legendary: { label: 'Legendary', color: '#fbbf24', bg: 'rgba(251,191,36,0.18)' },
};

export const OUTFIT_COLORS = [
  { id: 'black', hex: '#22201d' },
  { id: 'brown', hex: '#6b4a2f' },
  { id: 'blonde', hex: '#e8c56f' },
  { id: 'red', hex: '#c0392b' },
  { id: 'white', hex: '#e8e8e8' },
  { id: 'blue', hex: '#3b82f6' },
  { id: 'pink', hex: '#ec4899' },
  { id: 'green', hex: '#22c55e' },
  { id: 'purple', hex: '#8b5cf6' },
  { id: 'orange', hex: '#f97316' },
  { id: 'teal', hex: '#14b8a6' },
  { id: 'gray', hex: '#6b7280' },
];

export const HAIR_ITEMS = [
  { id: 'none', name: 'None', price: 0 },
  { id: 'hair_short', name: 'Short', price: 0 },
  { id: 'hair_spiky', name: 'Spiky', price: 0 },
  { id: 'hair_long', name: 'Long', price: 0 },
  { id: 'hair_bob', name: 'Bob', price: 0 },
  { id: 'hair_ponytail', name: 'Ponytail', price: 0 },
  { id: 'hair_buns', name: 'Space Buns', price: 0 },
  { id: 'hair_curly', name: 'Curly', price: 50 },
  { id: 'hair_mohawk', name: 'Mohawk', price: 75 },
  { id: 'hair_afro', name: 'Afro', price: 75 },
  { id: 'hair_fire', name: 'Fire Hair', price: 200, animated: true },
];

export const FACE_ITEMS = [
  { id: 'none', name: 'None', price: 0 },
  { id: 'face_round_glasses', name: 'Round Glasses', price: 0 },
  { id: 'face_mustache', name: 'Mustache', price: 0 },
  { id: 'face_beard', name: 'Beard', price: 0 },
  { id: 'face_shades', name: 'Shades', price: 50 },
  { id: 'face_clown_nose', name: 'Clown Nose', price: 50 },
  { id: 'face_monocle', name: 'Monocle', price: 50 },
  { id: 'face_warpaint', name: 'War Paint', price: 75 },
  { id: 'face_eyepatch', name: 'Eye Patch', price: 75 },
  { id: 'face_ski_mask', name: 'Ski Mask', price: 100 },
  { id: 'face_rainbow_visor', name: 'Rainbow Visor', price: 150, animated: true },
  { id: 'face_laser_eyes', name: 'Laser Eyes', price: 250, animated: true },
];

export const HAT_ITEMS = [
  { id: 'none', name: 'None', price: 0 },
  { id: 'hat_cap', name: 'Cap', price: 0 },
  { id: 'hat_beanie', name: 'Beanie', price: 0 },
  { id: 'hat_party', name: 'Party Hat', price: 50 },
  { id: 'hat_headphones', name: 'Headphones', price: 75 },
  { id: 'hat_propeller', name: 'Propeller Cap', price: 75 },
  { id: 'hat_chef', name: 'Chef Hat', price: 100 },
  { id: 'hat_cowboy', name: 'Cowboy Hat', price: 100 },
  { id: 'hat_tophat', name: 'Top Hat', price: 100 },
  { id: 'hat_viking', name: 'Viking Helm', price: 100 },
  { id: 'hat_halo', name: 'Halo', price: 150 },
  { id: 'hat_crown', name: 'Crown', price: 150 },
  { id: 'hat_flame_crown', name: 'Flame Crown', price: 200, animated: true },
  { id: 'hat_thundercloud', name: 'Thundercloud', price: 200, animated: true },
  { id: 'hat_ufo', name: 'Mini UFO', price: 250, animated: true },
];

export const SHIRT_ITEMS = [
  { id: 'none', name: 'None', price: 0 },
  { id: 'shirt_tee', name: 'T-Shirt', price: 0 },
  { id: 'shirt_tank', name: 'Tank Top', price: 0 },
  { id: 'shirt_vest', name: 'Vest', price: 50 },
  { id: 'shirt_hoodie', name: 'Hoodie', price: 75 },
  { id: 'shirt_jacket', name: 'Jacket', price: 100 },
  { id: 'shirt_suit', name: 'Suit + Tie', price: 150 },
  { id: 'shirt_neon_pulse', name: 'Neon Pulse Tee', price: 150, animated: true },
];

export const PANTS_ITEMS = [
  { id: 'none', name: 'None', price: 0 },
  { id: 'pants_jeans', name: 'Jeans', price: 0 },
  { id: 'pants_shorts', name: 'Shorts', price: 0 },
  { id: 'pants_track', name: 'Track Pants', price: 50 },
  { id: 'pants_cargo', name: 'Cargo Pants', price: 50 },
  { id: 'pants_camo', name: 'Camo Pants', price: 75 },
  { id: 'pants_formal', name: 'Formal Slacks', price: 100 },
  { id: 'pants_lava', name: 'Lava Pants', price: 150, animated: true },
];

export const TURTLE_MAN = {
  id: 'special_turtle_man',
  name: 'Turtle Man',
  price: 500,
  slug: 'turtle-man',
  description: 'Become the legendary chef-hat turtle. Full turtle mode — shell, chef hat and all — until you take it off.',
};

export const DISCO_HEAD = {
  id: 'special_disco_head',
  name: 'Disco Head',
  price: 350,
  slug: 'disco-head',
  description: 'Swap your head for a spinning mirror ball with real sparkles. The dance floor is wherever you stand.',
};

export const SPECIAL_ITEMS = [
  TURTLE_MAN,
  {
    id: 'special_dj_shark',
    name: 'DJ Shark',
    price: 600,
    slug: 'dj-shark',
    description: 'Become a great white with purple headphones and a gold DJ chain. Apex predator of the dance floor.',
  },
  {
    id: 'special_party_gorilla',
    name: 'Party Gorilla',
    price: 600,
    slug: 'party-gorilla',
    description: 'A burly gorilla in gold shades, gold chain and purple shorts. Nobody skips YOUR song requests.',
  },
  {
    id: 'special_disco_yeti',
    name: 'Disco Yeti',
    price: 550,
    slug: 'disco-yeti',
    description: 'A fluffy glow-stick-wielding yeti in a disco vest. Cold outside, heat on the floor.',
  },
  DISCO_HEAD,
  {
    id: 'special_rainbow_aura',
    name: 'Rainbow Aura',
    price: 400,
    slug: 'rainbow-aura',
    description: 'A ring of glowing rainbow orbs orbits your body wherever you go. Certified main-character energy.',
  },
  {
    id: 'special_butterflies',
    name: 'Butterfly Swarm',
    price: 350,
    slug: 'butterflies',
    description: 'A swarm of animated butterflies flutters around you, wings flapping in real time.',
  },
];

// avatar field each category writes to
export const CATEGORY_FIELD = {
  hair: 'hairStyle',
  face: 'faceItem',
  hat: 'hatItem',
  shirt: 'shirtItem',
  pants: 'pantsItem',
  special: 'special',
};
